import { useState } from "react";
import { Zap, AlertTriangle, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import AIEventGuidance from "@/components/ai/AIEventGuidance";
import type { ActiveEvent, EventOption } from "@/hooks/useEventEngine";

interface EventCardProps {
  event: ActiveEvent;
  onResolve: (choiceIndex: number) => Promise<EventOption | undefined>;
  onDismiss: () => void;
}

const EventCard = ({ event, onResolve, onDismiss }: EventCardProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const { template } = event;
  const isMoneyEvent = template.type === "money_event";

  const handleChoice = async (index: number) => {
    if (selectedIndex !== null) return;
    setResolving(true);
    setSelectedIndex(index);
    const option = await onResolve(index);
    if (option) setExplanation(option.explanation);
    setResolving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

      {/* Card */}
      <div
        className={`relative w-full max-w-md rounded-2xl border-2 p-6 space-y-5 shadow-2xl animate-fade-in ${
          isMoneyEvent
            ? "bg-card border-primary/40 shadow-[0_0_40px_hsl(var(--primary)/0.2)]"
            : "bg-card border-destructive/40 shadow-[0_0_40px_hsl(var(--destructive)/0.15)]"
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isMoneyEvent ? "bg-primary/15" : "bg-destructive/15"
            }`}
          >
            {isMoneyEvent ? (
              <DollarSign className="w-6 h-6 text-primary" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-destructive" />
            )}
          </div>
          <div>
            <span
              className={`text-[10px] font-semibold uppercase tracking-widest ${
                isMoneyEvent ? "text-primary" : "text-destructive"
              }`}
            >
              {isMoneyEvent ? "Money Event" : "Life Event"}
            </span>
            <h2 className="font-heading font-bold text-lg text-foreground">{template.title}</h2>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed">{template.description}</p>

        {/* AI Guidance */}
        {selectedIndex === null && (
          <AIEventGuidance
            eventTitle={template.title}
            eventDescription={template.description}
            eventType={isMoneyEvent ? "money_event" : "life_event"}
          />
        )}

        {/* Options */}
        {selectedIndex === null && (
          <div className="space-y-2">
            {template.options.map((option, i) => (
              <button
                key={i}
                onClick={() => handleChoice(i)}
                disabled={resolving}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-all duration-200 ${
                  isMoneyEvent
                    ? "border-primary/20 hover:border-primary/50 hover:bg-primary/5"
                    : "border-destructive/20 hover:border-destructive/50 hover:bg-destructive/5"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{option.label}</span>
                  <div className="flex items-center gap-1">
                    {option.amount > 0 && (
                      <span
                        className={`text-xs font-semibold ${
                          option.isDeduction ? "text-destructive" : "text-emerald-500"
                        }`}
                      >
                        {option.isDeduction ? "-" : "+"}${option.amount}
                      </span>
                    )}
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Explanation */}
        {explanation && (
          <div className="space-y-4 animate-fade-in">
            <div
              className={`rounded-xl p-4 ${
                isMoneyEvent ? "bg-primary/5 border border-primary/20" : "bg-destructive/5 border border-destructive/20"
              }`}
            >
              <div className="flex items-start gap-2">
                <Zap className={`w-4 h-4 mt-0.5 shrink-0 ${isMoneyEvent ? "text-primary" : "text-destructive"}`} />
                <p className="text-sm text-foreground leading-relaxed">{explanation}</p>
              </div>
            </div>
            <Button onClick={onDismiss} className="w-full">
              Continue Learning →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;
